import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Group,
    Loader,
    Paper,
    Progress,
    SimpleGrid,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core'
import {IconDeviceAnalytics, IconInfoCircle} from '@tabler/icons-react'
import {useMemo} from 'react'

import type {EquipmentUtilizationRow} from './types'
import {EQUIPMENT_UTILIZATION_HELP_TEXT} from './types'

type EquipmentUtilizationCardProps = {
    equipment: EquipmentUtilizationRow[]
    isLoading: boolean
    isError: boolean
    error: unknown
    onRetry?: () => void
}

const EQUIPMENT_TYPE_COLORS = [
    'brand',
    'teal',
    'orange',
    'grape',
    'cyan',
    'red',
    'indigo',
    'violet',
] as const

type EquipmentTypeSegment = EquipmentUtilizationRow & {
    color: (typeof EQUIPMENT_TYPE_COLORS)[number]
    utilizedShareOfFleetPercent: number
    utilizationPercent: number
}

type EquipmentFleetSummary = {
    fleetTotal: number
    fleetUtilized: number
    fleetAvailable: number
    utilizationPercent: number
    unusedShareOfFleetPercent: number
    segments: EquipmentTypeSegment[]
}

export function buildEquipmentFleetSummary(
    equipment: EquipmentUtilizationRow[],
): EquipmentFleetSummary {
    const fleetTotal = equipment.reduce((sum, entry) => sum + entry.total, 0)
    const fleetUtilized = equipment.reduce((sum, entry) => sum + entry.utilized, 0)
    const fleetAvailable = fleetTotal - fleetUtilized
    const utilizationPercent = fleetTotal === 0 ? 0 : Math.round((fleetUtilized / fleetTotal) * 100)
    const unusedShareOfFleetPercent = fleetTotal === 0 ? 0 : (fleetAvailable / fleetTotal) * 100
    const segments = equipment.map((entry, index) => ({
        ...entry,
        color: EQUIPMENT_TYPE_COLORS[index % EQUIPMENT_TYPE_COLORS.length],
        utilizedShareOfFleetPercent: fleetTotal === 0 ? 0 : (entry.utilized / fleetTotal) * 100,
        utilizationPercent:
            entry.total === 0 ? 0 : Math.round((entry.utilized / entry.total) * 100),
    }))

    return {
        fleetTotal,
        fleetUtilized,
        fleetAvailable,
        utilizationPercent,
        unusedShareOfFleetPercent,
        segments,
    }
}

function cardTitle() {
    return (
        <Group gap={4} wrap="nowrap">
            <Text fw={600} size="lg">
                Equipment utilization
            </Text>
            <Tooltip label={EQUIPMENT_UTILIZATION_HELP_TEXT} multiline w={320}>
                <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="dimmed"
                    aria-label="Equipment utilization definitions"
                >
                    <IconInfoCircle size={14} stroke={1.5} />
                </ActionIcon>
            </Tooltip>
        </Group>
    )
}

function EquipmentTypeDetail({segment}: {segment: EquipmentTypeSegment}) {
    return (
        <Box>
            <Text tt="uppercase" fz="xs" c="dimmed" fw={700}>
                {segment.typeName}
            </Text>
            <Group justify="space-between" align="flex-end" gap={0} mt="xs">
                <Text fw={700} fz="lg" lh={1}>
                    {segment.utilized} / {segment.total}
                </Text>
                <Text c={segment.color} fw={700} fz="sm" lh={1}>
                    {segment.utilizationPercent}%
                </Text>
            </Group>
        </Box>
    )
}

function EquipmentFleetChart({summary}: {summary: EquipmentFleetSummary}) {
    const utilizedSegments = summary.segments
        .filter((segment) => segment.utilized > 0)
        .map((segment) => (
            <Progress.Section
                key={segment.rentalAssetTypeId}
                value={segment.utilizedShareOfFleetPercent}
                color={segment.color}
                aria-label={`${segment.typeName} utilized`}
            />
        ))

    return (
        <>
            <Group justify="space-between" mt="md">
                <Group align="baseline" gap="xs" wrap="wrap">
                    <Text fz="xl" fw={700} lh={1}>
                        {summary.fleetUtilized}
                    </Text>
                    <Text c="dimmed" fz="sm" mt="xs">
                        of {summary.fleetTotal} fleet assets · {summary.utilizationPercent}% in use
                    </Text>
                </Group>
                <IconDeviceAnalytics size={22} color="var(--mantine-color-dimmed)" stroke={1.5} />
            </Group>

            <Progress.Root size="xl" mt="lg">
                {utilizedSegments}
                {summary.fleetAvailable > 0 && (
                    <Progress.Section
                        value={summary.unusedShareOfFleetPercent}
                        color="gray.4"
                        aria-label="Available"
                    />
                )}
            </Progress.Root>

            <SimpleGrid cols={{base: 1, xs: 2, md: 4}} mt="md">
                {summary.segments.map((segment) => (
                    <EquipmentTypeDetail key={segment.rentalAssetTypeId} segment={segment} />
                ))}
            </SimpleGrid>
        </>
    )
}

export function EquipmentUtilizationCard({
    equipment,
    isLoading,
    isError,
    error,
    onRetry,
}: EquipmentUtilizationCardProps) {
    const summary = useMemo(() => buildEquipmentFleetSummary(equipment), [equipment])

    if (isLoading) {
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Group justify="center" py="md">
                        <Loader size="sm" />
                    </Group>
                </Stack>
            </Paper>
        )
    }

    if (isError) {
        const message =
            error instanceof Error ? error.message : 'Failed to load equipment utilization'
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Alert color="red" title="Could not load equipment utilization">
                        {message}
                    </Alert>
                    {onRetry && (
                        <Button onClick={onRetry} variant="light">
                            Retry
                        </Button>
                    )}
                </Stack>
            </Paper>
        )
    }

    if (equipment.length === 0) {
        return (
            <Paper withBorder p="xl" radius="md">
                <Stack gap="md">
                    {cardTitle()}
                    <Text c="dimmed" fz="sm">
                        No equipment types to display.
                    </Text>
                </Stack>
            </Paper>
        )
    }

    return (
        <Paper withBorder p="xl" radius="md">
            <Stack gap="md">
                {cardTitle()}
                <EquipmentFleetChart summary={summary} />
            </Stack>
        </Paper>
    )
}
